// src/offlineTiles.js
export default class OfflineTileManager {
  constructor(lat, lng, radius, tileUrlTemplate) {
    this.lat = lat;
    this.lng = lng;
    this.radius = radius;
    this.tileUrlTemplate = tileUrlTemplate;
  }

  isCordova() {
    return typeof window !== "undefined" && !!window.cordova;
  }

  async isOnline() {
    // Fallback für Browser oder Cordova ohne Network-Plugin
    if (!this.isCordova() || !navigator.connection || typeof Connection === "undefined") {
      console.log("[OfflineTiles] Kein Cordova-Network-Plugin → fallback auf navigator.onLine =", navigator.onLine);
      return navigator.onLine;
    }

    console.log("[OfflineTiles] Cordova Network Type:", navigator.connection.type);
    return navigator.connection.type !== Connection.NONE;
  }

  getTilesInRadius(zoom) {
    const tiles = [];
    const earthCircumference = 40075016.686;
    const metersPerPixel =
      earthCircumference * Math.cos(this.lat * Math.PI / 180) / Math.pow(2, zoom + 8);
    const pixelRadius = this.radius / metersPerPixel;

    const tileX = Math.floor((this.lng + 180) / 360 * Math.pow(2, zoom));
    const tileY = Math.floor(
      (1 -
        Math.log(
          Math.tan(this.lat * Math.PI / 180) +
            1 / Math.cos(this.lat * Math.PI / 180)
        ) /
          Math.PI) /
        2 *
        Math.pow(2, zoom)
    );

    const tileRadius = Math.ceil(pixelRadius / 256);

    for (let x = tileX - tileRadius; x <= tileX + tileRadius; x++) {
      for (let y = tileY - tileRadius; y <= tileY + tileRadius; y++) {
        tiles.push({ x, y, z: zoom });
      }
    }

    console.log(`[OfflineTiles] Zoom ${zoom}: berechnete Tiles = ${tiles.length}`);
    return tiles;
  }

  async downloadTiles(zoomLevels = [16, 17, 18]) {
    if (!this.isCordova()) {
      console.warn("[OfflineTiles] Cordova nicht verfügbar – Offline-Download wird im Browser übersprungen.");
      return;
    }

    console.log("[OfflineTiles] Starte Download…");

    const baseDir = cordova.file.dataDirectory + "tiles/";
    await this.ensureDir(baseDir);

    for (const zoom of zoomLevels) {
      const tiles = this.getTilesInRadius(zoom);

      console.log(`[OfflineTiles] Lade ${tiles.length} Tiles für Zoom ${zoom}`);

      for (const tile of tiles) {
        const url = this.tileUrlTemplate
          .replace("{z}", tile.z)
          .replace("{x}", tile.x)
          .replace("{y}", tile.y);

        const filePath = `${baseDir}${tile.z}_${tile.x}_${tile.y}.png`;

        try {
          await this.downloadFile(url, filePath);
        } catch (err) {
          console.error("[OfflineTiles] Fehler beim Download:", url, err);
        }
      }
    }

    console.log("[OfflineTiles] Download abgeschlossen.");
  }

  ensureDir() {
    return new Promise((resolve, reject) => {
      window.resolveLocalFileSystemURL(
        cordova.file.dataDirectory,
        dir => {
          dir.getDirectory("tiles", { create: true }, resolve, reject);
        },
        reject
      );
    });
  }

  downloadFile(url, filePath) {
    return new Promise((resolve, reject) => {
      if (!window.FileTransfer) {
        console.error("[OfflineTiles] FileTransfer Plugin fehlt!");
        reject("FileTransfer missing");
        return;
      }

      const ft = new FileTransfer();
      console.log("[OfflineTiles] Lade:", url);

      ft.download(
        url,
        filePath,
        entry => {
          console.log("[OfflineTiles] Gespeichert:", entry.toURL());
          resolve(entry);
        },
        err => {
          console.error("[OfflineTiles] Download-Fehler:", err);
          reject(err);
        }
      );
    });
  }

  async getOfflineTile(z, x, y) {
    if (!this.isCordova()) return null;

    const filePath = `${cordova.file.dataDirectory}tiles/${z}_${x}_${y}.png`;

    return new Promise(resolve => {
      window.resolveLocalFileSystemURL(
        filePath,
        fileEntry => {
          fileEntry.file(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
              console.log("[OfflineTiles] Offline-Tile gefunden:", z, x, y);
              resolve(reader.result);
            };
            reader.readAsDataURL(file);
          });
        },
        () => {
          // Kein Offline-Tile vorhanden
          resolve(null);
        }
      );
    });
  }
}
